import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { BookOpen, TrendingUp, Award, Play, Clock, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    averageProgress: 0,
    totalWatchTime: 0
  });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [i18n.language]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('/users/dashboard', {
        params: { lang: i18n.language }
      });
      const coursesData = response.data.courses || [];
      setCourses(coursesData);

      console.log('📊 Données des cours:', coursesData);

      // Calculer les statistiques
      const totalCourses = coursesData.length;
      const completedCourses = coursesData.filter(c => c.progress.isCompleted).length;
      const averageProgress = totalCourses > 0 
        ? Math.round(coursesData.reduce((sum, c) => sum + c.progress.progressPercent, 0) / totalCourses)
        : 0;
      const totalWatchTime = coursesData.reduce((sum, c) => {
        console.log(`  Cours: ${c.course.title}, watchedMinutes: ${c.progress.watchedMinutes || 0}`);
        return sum + (c.progress.watchedMinutes || 0);
      }, 0);

      console.log('⏱️ Temps total de visionnage:', totalWatchTime, 'minutes');

      setStats({
        totalCourses,
        completedCourses,
        averageProgress,
        totalWatchTime
      });
    } catch (error) {
      console.error('Erreur lors du chargement du tableau de bord:', error);
      toast.error(t('dashboard.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    }
    return `${mins}min`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('dashboard.title')}</h1>
        
        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">{t('dashboard.myCourses')}</h3>
              <BookOpen className="w-5 h-5 text-primary-600" />
            </div>
            <div className="mt-2">
              <span className="text-3xl font-bold text-gray-900">{stats.totalCourses}</span>
              <p className="text-sm text-gray-500 mt-1">{t('dashboard.coursesPurchased')}</p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">{t('dashboard.averageProgress')}</h3>
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="mt-2">
              <span className="text-3xl font-bold text-gray-900">{stats.averageProgress}%</span>
              <p className="text-sm text-gray-500 mt-1">{t('dashboard.ofProgress')}</p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">{t('dashboard.completedCourses')}</h3>
              <Award className="w-5 h-5 text-green-600" />
            </div>
            <div className="mt-2">
              <span className="text-3xl font-bold text-gray-900">{stats.completedCourses}</span>
              <p className="text-sm text-gray-500 mt-1">{t('dashboard.coursesCompleted')}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">{t('dashboard.watchTime')}</h3>
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div className="mt-2">
              <span className="text-3xl font-bold text-gray-900">{formatDuration(stats.totalWatchTime)}</span>
              <p className="text-sm text-gray-500 mt-1">{t('dashboard.totalTime')}</p>
            </div>
          </div>
        </div>
        
        {/* Progression par cours */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{t('dashboard.progressByCourse')}</h2>
            <Link to="/my-courses" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              {t('dashboard.viewAllCourses')}
            </Link>
          </div>
          
          {courses.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">{t('dashboard.noCoursesPurchased')}</p>
              <Link to="/courses" className="btn btn-primary">
                {t('dashboard.discoverCourses')}
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {courses.map(({ course, progress }) => (
                <div key={course.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{course.title}</h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <Play className="w-4 h-4 mr-1" />
                        {course.videoCount} {t('courses.videos')}
                        <span className="mx-2">•</span>
                        <Clock className="w-4 h-4 mr-1" />
                        {formatDuration(course.duration)}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {progress.isCompleted ? (
                        <div className="flex items-center text-green-600 font-medium">
                          <CheckCircle className="w-5 h-5 mr-1" />
                          {t('dashboard.completed')}
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-primary-600">{Math.round(progress.progressPercent)}%</span>
                      )}
                      <Link
                        to={`/watch/${course.id}`}
                        className="btn btn-sm btn-primary"
                      >
                        {progress.progressPercent > 0 ? t('dashboard.continue') : t('dashboard.start')}
                      </Link>
                    </div>
                  </div>
                  
                  {/* Barre de progression */}
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        progress.isCompleted ? 'bg-green-600' : 'bg-primary-600'
                      }`}
                      style={{ width: `${Math.round(progress.progressPercent)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
